import { Fragment, useEffect, useRef, useState } from 'react'
import { AlertCircleIcon, CheckIcon, ChevronRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { AgentStep, ReasoningStep, ToolStep } from '@hub/contracts'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { collapsePanel, ShimmerLabel, SwapLabel } from '@/components/assistant-ui/elements/surfaces'
import { cn } from '@/lib/utils'
import { ToolCall } from '@/components/assistant-ui/elements/tool-call'
import { ToolError } from '@/components/assistant-ui/elements/tool-error'
import {
  ReasoningContent,
  ReasoningRoot,
  ReasoningText,
  ReasoningTrigger,
} from '@/components/assistant-ui/elements/reasoning'
import {
  describeRequest,
  describeResult,
  toolMeta,
  toolQuery,
  toolRequestText,
  toolResultText,
} from './tool-meta'

interface AgentStepsProps {
  steps: AgentStep[]
  /** The turn is still being produced — tool groups stay expanded meanwhile. */
  active?: boolean
  /** Re-run the turn (offered on failed tool calls). */
  onRetry?: () => void
  retrying?: boolean
}

function durationSeconds(step: AgentStep): number | undefined {
  if (!step.completedAt) return undefined
  const ms = Date.parse(step.completedAt) - Date.parse(step.startedAt)
  return ms > 0 ? Math.round(ms / 1000) : undefined
}

function ReasoningEntry({ step }: { step: ReasoningStep }) {
  const streaming = step.status === 'running'
  return (
    <ReasoningRoot variant="ghost" streaming={streaming} className="mb-1">
      <ReasoningTrigger active={streaming} duration={durationSeconds(step)} />
      <ReasoningContent>
        <ReasoningText>
          <p className="whitespace-pre-wrap">{step.text || '…'}</p>
        </ReasoningText>
      </ReasoningContent>
    </ReasoningRoot>
  )
}

function ToolEntry({
  step,
  onRetry,
  retrying,
}: {
  step: ToolStep
  onRetry?: () => void
  retrying?: boolean
}) {
  const { t } = useTranslation('chat')
  const [open, setOpen] = useState(false)
  const meta = toolMeta(step.toolName)
  const requestRows = describeRequest(step)
  const result = describeResult(step)

  if (step.status === 'error') {
    return (
      <ToolError
        name={step.toolName}
        target={toolQuery(step) || meta.label}
        message={step.error?.message ?? t('errors.toolFailed')}
        attempt={1}
        maxAttempts={1}
        retrying={!!retrying}
        onRetry={onRetry}
        className="max-w-none"
      />
    )
  }

  return (
    <ToolCall
      label={meta.label}
      activeLabel={meta.activeLabel}
      query={toolQuery(step)}
      request={
        requestRows.length ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
            {requestRows.map((row) => (
              <Fragment key={row.label}>
                <dt className="text-foreground/40">{row.label}</dt>
                <dd className="truncate">{row.value}</dd>
              </Fragment>
            ))}
          </dl>
        ) : (
          <span className="text-foreground/40">{t('steps.noParams')}</span>
        )
      }
      requestJson={step.args !== undefined ? toolRequestText(step) : undefined}
      result={
        <div className="flex flex-col gap-0.5">
          <span>{result?.summary ?? t('steps.done')}</span>
          {result?.lines && result.lines.length > 0 && (
            <ul className="text-foreground/55 list-inside list-disc">
              {result.lines.map((line, i) => (
                <li key={i} className="truncate">
                  {line}
                </li>
              ))}
            </ul>
          )}
        </div>
      }
      resultJson={step.resultPreview ? toolResultText(step) : undefined}
      running={step.status === 'running'}
      open={open}
      onOpenChange={setOpen}
      className="max-w-none"
    />
  )
}

/** Runs of this many consecutive tool calls (or more) collapse into a group. */
const GROUP_MIN_TOOLS = 3

type Segment = { kind: 'step'; step: AgentStep } | { kind: 'group'; id: string; steps: ToolStep[] }

/** Split the timeline into single steps and groupable runs of tool calls. */
function segmentSteps(steps: AgentStep[]): Segment[] {
  const out: Segment[] = []
  let run: ToolStep[] = []
  const flush = () => {
    if (run.length >= GROUP_MIN_TOOLS) out.push({ kind: 'group', id: run[0]!.id, steps: run })
    else for (const step of run) out.push({ kind: 'step', step })
    run = []
  }
  for (const step of steps) {
    if (step.kind === 'tool') run.push(step)
    else {
      flush()
      out.push({ kind: 'step', step })
    }
  }
  flush()
  return out
}

/** "Sökte i dokument ×5, Letade dokument ×2" — what a collapsed group did. */
function groupSummary(steps: ToolStep[]): string {
  const counts = new Map<string, number>()
  for (const step of steps) {
    const label = toolMeta(step.toolName).label
    counts.set(label, (counts.get(label) ?? 0) + 1)
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, n]) => (n > 1 ? `${label} ×${n}` : label))
    .join(', ')
}

function ToolGroup({
  steps,
  active,
  onRetry,
  retrying,
}: {
  steps: ToolStep[]
  active: boolean
  onRetry?: () => void
  retrying?: boolean
}) {
  const { t } = useTranslation('chat')
  const running = steps.some((s) => s.status === 'running')
  const failed = steps.some((s) => s.status === 'error')
  const working = active || running
  // Open while the agent works; a manual toggle wins until the turn finishes,
  // at which point the group folds up (unless something failed).
  const [manual, setManual] = useState<boolean | null>(null)
  const wasWorking = useRef(working)
  useEffect(() => {
    if (wasWorking.current && !working) setManual(null)
    wasWorking.current = working
  }, [working])
  const open = manual ?? (working || failed)
  const done = steps.filter((s) => s.status !== 'running').length

  return (
    <Collapsible data-slot="tool-group" open={open} onOpenChange={setManual} className="w-full">
      <CollapsibleTrigger className="group/trigger text-foreground/55 hover:text-foreground/90 flex w-full items-center gap-2 rounded-md py-1 text-[13.5px] transition-colors outline-none">
        <ChevronRightIcon className="size-3.5 shrink-0 opacity-60 transition-transform duration-200 ease-[cubic-bezier(0.32,0.72,0,1)] group-data-open/trigger:rotate-90 group-data-panel-open/trigger:rotate-90 motion-reduce:transition-none" />
        <SwapLabel active={running ? 0 : 1} className="shrink-0 text-start tabular-nums">
          <ShimmerLabel active={running} className="relative inline-block leading-none">
            {t('steps.usingTools', { done, total: steps.length })}
          </ShimmerLabel>
          <>{t('steps.tools', { total: steps.length })}</>
        </SwapLabel>
        {!open && (
          <span className="text-foreground/35 fade-in animate-in min-w-0 truncate text-xs duration-200">
            {groupSummary(steps)}
          </span>
        )}
        <span className="ms-auto flex w-4 shrink-0 items-center justify-end">
          {failed ? (
            <AlertCircleIcon className="size-3.5 text-red-500" />
          ) : (
            !running && (
              <CheckIcon className="fade-in zoom-in-90 animate-in size-3.5 text-emerald-500 duration-200" />
            )
          )}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className={cn(collapsePanel, 'outline-none')}>
        <div className="border-foreground/10 ms-[6px] flex flex-col gap-0.5 border-s ps-3">
          {steps.map((step) => (
            <ToolEntry key={step.id} step={step} onRetry={onRetry} retrying={retrying} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

/**
 * Ordered activity timeline of one assistant turn (reasoning + tool calls).
 * Long runs of tool calls fold into a "Verktyg (n)" group that is expanded
 * while the turn is in progress and collapses once it is done.
 */
export function AgentSteps({ steps, active = false, onRetry, retrying }: AgentStepsProps) {
  if (steps.length === 0) return null
  return (
    <div data-slot="agent-steps" className="flex flex-col gap-0.5">
      {segmentSteps(steps).map((segment) =>
        segment.kind === 'group' ? (
          <ToolGroup
            key={segment.id}
            steps={segment.steps}
            active={active}
            onRetry={onRetry}
            retrying={retrying}
          />
        ) : segment.step.kind === 'reasoning' ? (
          <ReasoningEntry key={segment.step.id} step={segment.step} />
        ) : (
          <ToolEntry
            key={segment.step.id}
            step={segment.step}
            onRetry={onRetry}
            retrying={retrying}
          />
        )
      )}
    </div>
  )
}
